import {
  CalendarDays, Users, FileText, CreditCard, BarChart3,
  Coins, Smartphone, MapPin, Package,
} from "lucide-react";

/**
 * Nine-cell feature grid.
 *
 * Copy strategy:
 *   - Each card leads with a verb ("Plan", "Dispatch", "Invoice") not a
 *     noun ("Calendar", "Staff"). Verbs sell outcomes, nouns sell
 *     features. Straight from the 2026 SaaS copy research.
 *   - Sub-copy specifies the concrete mechanism (7-day calendar, PIN
 *     login, PDF templates) so a trade owner can immediately picture it.
 *
 * Visual treatment: icon in accent tint, hover promotes the whole card
 * with border-accent + subtle lift. Cards stay compact (< 50 lines each).
 */
const FEATURES = [
  {
    icon: CalendarDays,
    slug: "plan",
    title: "Plan the week",
    desc: "Drag jobs across the calendar. Day, Week, and Month views. Multi-staff, multi-site.",
  },
  {
    icon: Users,
    slug: "dispatch",
    title: "Dispatch staff",
    desc: "Each staff member gets a PIN-locked phone URL. They see only their jobs, a rolling 4-day window.",
  },
  {
    icon: FileText,
    slug: "invoice",
    title: "Issue invoices",
    desc: "VAT · RCT · Cash-with-invoice · Simple cash. PDF templates you control, emailed direct.",
  },
  {
    icon: CreditCard,
    slug: "payments",
    title: "Track payments",
    desc: "Card, transfer, cash-to-driver, or cash-to-manager. See what's owed, what's late, who paid whom.",
  },
  {
    icon: Coins,
    slug: "cash",
    title: "Cash reconciliation",
    desc: "Bank-statement ledger for every staff member. Settle to zero and auto-post to your books.",
  },
  {
    icon: Smartphone,
    slug: "mobile",
    title: "Mobile-first for staff",
    desc: "No app store install. Works on any phone. Mark completed, take a photo, log the cash — done.",
  },
  {
    icon: BarChart3,
    slug: "reports",
    title: "Know your numbers",
    desc: "Customer 360, aged debt, funds by method, VAT-ready exports. No spreadsheets required.",
  },
  {
    icon: MapPin,
    slug: "routing",
    title: "Route + travel time",
    desc: "ORS-powered distance and drive-time between jobs. Plan the round trip, not each stop.",
  },
  {
    icon: Package,
    slug: "products",
    title: "Track parts & stock",
    desc: "Hand parts out to your staff, watch stock deduct automatically when they add products to a job, split invoiced vs off-the-books cleanly.",
  },
];

export default function FeaturesGrid() {
  return (
    <section
      id="features"
      className="bg-background scroll-mt-20"
      data-testid="landing-features"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">
            // Everything to run the day
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
            One tool, from the booking to the bank.
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Nine capabilities. Zero add-ons. All included from the €29 tier up.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-12">
          {FEATURES.map((f) => (
            <article
              key={f.slug}
              data-testid={`feature-card-${f.slug}`}
              className="group relative bg-card border border-border rounded-xl p-6 hover:border-accent/70 hover:shadow-lg hover:-translate-y-1 transition-all reveal reveal-delay-hover"
            >
              <div className="h-11 w-11 rounded-lg bg-accent/10 text-accent grid place-items-center group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-black text-lg tracking-tight">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                {f.desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
